import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Фирменный иконко-сет founder (Figma «Common — Icons», O63r54lNNbE8GmYOsk55pa).
 * SVG лежат в src/assets/brand-icons/ и загружаются витовским glob'ом как raw:
 *  - `<name>`        — системный сет 16px (search, machine, visit, pharmacy, …)
 *  - `crop-<name>`   — иконки культур 32px (crop-wheat-winter-soft, crop-corn, …)
 *  - `nav24-<name>`  — навбарный сет 24px (nav24-navbar-farm, nav24-globe, …)
 * Все глифы тема-безопасны: рисунок = currentColor, выбивки/блики = var(--card)
 * (фон текущей темы) — работают в light/dark без белых плашек. Размер задаётся пропом size (или className).
 * Отсутствующий глиф — добираем из того же Figma-файла, НЕ рисуем свой
 * (см. frontend-vite/CLAUDE.md, раздел «Иконки»).
 */
const rawIcons = import.meta.glob('../../assets/brand-icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const iconByName = new Map<string, string>();
for (const [path, raw] of Object.entries(rawIcons)) {
  const name = path.split('/').pop()!.replace(/\.svg$/, '');
  iconByName.set(name, raw);
}

export const brandIconNames = [...iconByName.keys()].sort();

export interface BrandIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Имя файла без .svg: "search", "crop-corn", "nav24-globe" */
  name: string;
  /** Размер в px (width=height). По умолчанию 16. */
  size?: number;
}

export const BrandIcon = React.forwardRef<HTMLSpanElement, BrandIconProps>(
  ({ name, size = 16, className, style, ...props }, ref) => {
    const raw = iconByName.get(name);
    if (!raw) {
      if (import.meta.env.DEV) {
        console.warn(`BrandIcon: нет глифа «${name}» — добери из Figma-сета`);
      }
      return null;
    }
    return (
      <span
        ref={ref}
        role="img"
        aria-hidden={props['aria-label'] ? undefined : true}
        className={cn('inline-flex shrink-0 [&>svg]:h-full [&>svg]:w-full', className)}
        style={{ width: size, height: size, ...style }}
        dangerouslySetInnerHTML={{ __html: raw }}
        {...props}
      />
    );
  },
);
BrandIcon.displayName = 'BrandIcon';
